import React, { useState, useMemo } from 'react';
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';

const UpdatePassword = ({ username, onPasswordUpdated }) => {
  const [newPassword, setNewPassword] = useState('');
  const [retypePassword, setRetypePassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showRetypePassword, setShowRetypePassword] = useState(false);

  const passwordStrength = useMemo(() => {
    let score = 0;
    let requirements = {
      length: false,
      number: false,
      special: false
    };

    if (newPassword.length >= 8) {
      score += 1;
      requirements.length = true;
    }
    if (/\d/.test(newPassword)) {
      score += 1;
      requirements.number = true;
    }
    if (/[^A-Za-z0-9]/.test(newPassword)) {
      score += 1;
      requirements.special = true;
    }

    let strengthText = '';
    let strengthColor = '';
    let barColor = '';

    if (score === 0) {
      strengthText = 'Poor';
      strengthColor = 'text-red-600';
      barColor = 'bg-red-500';
    } else if (score === 1 || score === 2) {
      strengthText = 'Almost there!';
      strengthColor = 'text-yellow-600';
      barColor = 'bg-yellow-500';
    } else {
      strengthText = 'Strong password!';
      strengthColor = 'text-green-600';
      barColor = 'bg-green-500';
    }

    return {
      score,
      percentage: (score / 3) * 100,
      requirements,
      strengthText,
      strengthColor,
      barColor
    };
  }, [newPassword]);

  const validatePassword = (password) => {
    if (password.length < 8) return false;
    if (!/\d/.test(password)) return false;
    if (!/[^A-Za-z0-9]/.test(password)) return false;
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (newPassword !== retypePassword) {
        throw new Error("The passwords don't match. Please try again.");
      }

      if (!validatePassword(newPassword)) {
        throw new Error("The password does not meet the requirements");
      }

      // Updated URL for mandatory password updates
      const response = await fetch('/api/update-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          username: username,
          new_password: newPassword
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        onPasswordUpdated();
      } else {
        throw new Error(data.error || 'Failed to update password');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-red-600 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden transition-all duration-300 ease-in-out">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-red-600 px-6 py-4">
          <div className="text-center">
            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-blue-600 font-bold text-lg">🔒</span>
            </div>
            <h2 className="text-xl font-bold text-white mb-1">
              Update Password
            </h2>
            <p className="text-blue-100 text-xs">
              Please update your password to continue
            </p>
          </div>
        </div>

        {/* Password Update Form */}
        <div className="px-6 py-4">
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <h3 className="font-medium text-blue-800 mb-2 text-sm">Password Requirements</h3>
            <ul className="text-xs text-blue-700 space-y-1">
              <li className={`flex items-center ${passwordStrength.requirements.length ? 'text-green-600' : ''}`}>
                <span className="mr-2">{passwordStrength.requirements.length ? '✓' : '•'}</span>
                At least 8 characters long
              </li>
              <li className={`flex items-center ${passwordStrength.requirements.number ? 'text-green-600' : ''}`}>
                <span className="mr-2">{passwordStrength.requirements.number ? '✓' : '•'}</span>
                Includes at least one number
              </li>
              <li className={`flex items-center ${passwordStrength.requirements.special ? 'text-green-600' : ''}`}>
                <span className="mr-2">{passwordStrength.requirements.special ? '✓' : '•'}</span>
                Includes at least one special character
              </li>
            </ul>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            {/* Password Strength Indicator */}
            <div className={`transition-all duration-300 ease-in-out overflow-hidden ${newPassword ? 'max-h-20 opacity-100 mb-4' : 'max-h-0 opacity-0 mb-0'
              }`}>
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">Password Strength</span>
                  <span className={`text-sm font-medium ${passwordStrength.strengthColor}`}>
                    {passwordStrength.strengthText}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${passwordStrength.barColor}`}
                    style={{ width: `${passwordStrength.percentage}%` }}
                  ></div>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  placeholder="Enter your new password"
                  required
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors focus:outline-none"
                  disabled={isLoading}
                  title={showNewPassword ? "Hide password" : "Show password"}
                >
                  {showNewPassword ? (
                    <MdVisibilityOff className="h-5 w-5" />
                  ) : (
                    <MdVisibility className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Retype New Password
              </label>
              <div className="relative">
                <input
                  type={showRetypePassword ? "text" : "password"}
                  value={retypePassword}
                  onChange={(e) => setRetypePassword(e.target.value)}
                  className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  placeholder="Retype your new password"
                  required
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowRetypePassword(!showRetypePassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors focus:outline-none"
                  disabled={isLoading}
                  title={showRetypePassword ? "Hide password" : "Show password"}
                >
                  {showRetypePassword ? (
                    <MdVisibilityOff className="h-5 w-5" />
                  ) : (
                    <MdVisibility className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            <button
              onClick={handleSubmit}
              disabled={isLoading || !newPassword || !retypePassword}
              className="w-full bg-gradient-to-r from-blue-600 to-red-600 hover:from-blue-700 hover:to-red-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 transform hover:scale-105 disabled:scale-100 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Updating Password...
                </div>
              ) : (
                'Update Password'
              )}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-3">
          <div className="flex justify-center text-xs text-gray-500">
            <span>AI ERD Assistant v1.0</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdatePassword;